import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Eye, Pencil, Building2, X, History, CheckSquare, Trash2, RotateCcw, UserX } from "lucide-react";
import { api, formatApiErrorDetail } from "../../api";
import { PageHeader, Table, Badge, Btn, dt } from "../../components/common";
import { Modal, Field, TextArea } from "./crmui";
import ContactDrawer from "../../components/ContactDrawer";
import { useAuth } from "../../auth";
import { useAutoRefresh } from "../../lib/autoRefresh";
import { HighlightText } from "../../lib/highlight";

const DEPARTAMENTOS = ["Geral", "CEO", "Financeiro", "DPO", "CISO", "TI", "Compras", "Legal", "Marketing", "Outro"];

export default function Contacts() {
  const nav = useNavigate();
  const { can } = useAuth();
  const isSuper = can("*");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [drawer, setDrawer] = useState(null);
  const [edit, setEdit] = useState(null);
  const [accountFilter, setAccountFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({ departamento: "", marketing_optin: null });
  const [historyFor, setHistoryFor] = useState(null); // {contact_id, nome, events}
  const [tab, setTab] = useState("ativos"); // ativos | inativos
  const [toDelete, setToDelete] = useState(null);
  const [bulkDeactivateOpen, setBulkDeactivateOpen] = useState(false);
  const [sortMode, setSortMode] = useState("alpha"); // alpha | recent

  const load = async () => {
    try {
      const params = { ativo: tab === "inativos" ? "0" : "1" };
      if (q) params.q = q;
      const { data } = await api.get("/crm/contacts", { params });
      setRows(data);
      setSelected(new Set());
      const params2 = new URLSearchParams(window.location.search);
      const eid = params2.get("edit_id");
      if (eid) {
        const found = (data || []).find((c) => c.id === eid);
        if (found) openEdit(found);
        params2.delete("edit_id");
        window.history.replaceState(null, "", "/app/crm/contactos" + (params2.toString() ? `?${params2}` : ""));
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [tab]);
  useAutoRefresh(load); // eslint-disable-line

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      const { data } = await api.delete(`/crm/contacts/${toDelete.id}`);
      if (data.eliminado) toast.success(`${toDelete.nome} eliminado definitivamente.`);
      else toast.message(`${toDelete.nome} tem atividade registada - foi apenas marcado como inativo.`);
      setToDelete(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reactivate = async (c) => {
    try {
      await api.post(`/crm/contacts/${c.id}/reactivate`);
      toast.success(`${c.nome} reativado.`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const confirmBulkDeactivate = async () => {
    try {
      const { data } = await api.post("/crm/contacts/bulk-deactivate", { ids: [...selected] });
      toast.success(`${data.modified} contacto${data.modified > 1 ? "s" : ""} marcado${data.modified > 1 ? "s" : ""} como inativo${data.modified > 1 ? "s" : ""}.`);
      setBulkDeactivateOpen(false); setSelected(new Set()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Lista de contas unicas para dropdown (derivada dos rows enriquecidos)
  const accountsOptions = useMemo(() => {
    const map = new Map();
    (rows || []).forEach((r) => {
      if (r.account_id) map.set(r.account_id, r.account_nome || r.account_id);
    });
    return [...map.entries()].map(([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let list = rows;
    if (accountFilter === "__none__") list = list.filter((r) => !r.account_id);
    else if (accountFilter) list = list.filter((r) => r.account_id === accountFilter);
    if (sortMode === "recent") list = [...list].sort((a, b) => new Date(b.criado_em || 0) - new Date(a.criado_em || 0));
    return list;
  }, [rows, accountFilter, sortMode]);

  const openEdit = (c) => {
    setEdit({
      ...c,
      email: (c.emails && c.emails[0]) || c.email || "",
      telefone: (c.telefones && c.telefones[0]) || c.telefone || "",
      telemovel: (c.telefones && c.telefones[1]) || c.telemovel || "",
    });
  };

  const openHistory = async (c) => {
    setHistoryFor({ contact_id: c.id, nome: c.nome, events: null });
    try {
      const { data } = await api.get(`/crm/contacts/${c.id}/history`);
      setHistoryFor({ contact_id: c.id, nome: c.nome, events: data.events || [] });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleSel = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleAll = () => {
    if (selected.size === filteredRows.length) setSelected(new Set());
    else setSelected(new Set(filteredRows.map((r) => r.id)));
  };

  const applyBulk = async () => {
    if (selected.size === 0) return toast.error("Seleccione contactos.");
    const payload = { ids: [...selected] };
    if (bulk.departamento) payload.departamento = bulk.departamento;
    if (bulk.marketing_optin !== null && bulk.marketing_optin !== "") payload.marketing_optin = bulk.marketing_optin === "true";
    if (!("departamento" in payload) && !("marketing_optin" in payload)) return toast.error("Indique um campo.");
    try {
      const { data } = await api.post("/crm/contacts/bulk-update", payload);
      toast.success(`${data.modified}/${data.matched} contactos actualizados.`);
      setBulkOpen(false); setBulk({ departamento: "", marketing_optin: null });
      setSelected(new Set()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    if (!edit.nome?.trim()) return toast.error("Nome obrigatorio.");
    try {
      const telefones = [edit.telefone, edit.telemovel].filter((x) => (x || "").trim());
      await api.put(`/crm/contacts/${edit.id}`, {
        nome: edit.nome, cargo: edit.cargo || "", departamento: edit.departamento || "",
        email: edit.email, emails: edit.email ? [edit.email] : [],
        telefone: edit.telefone, telemovel: edit.telemovel, telefones,
        notas: edit.notas || "", principal: !!edit.principal,
        marketing_optin: !!edit.marketing_optin,
      });
      toast.success("Contacto actualizado.");
      setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const columns = [
    { key: "_sel", label: (
      <input type="checkbox" checked={filteredRows.length > 0 && selected.size === filteredRows.length}
        onChange={toggleAll} data-testid="contacts-sel-all"
        title={`Seleccionar todos os ${filteredRows.length} contactos filtrados (nao so os visiveis nesta pagina)`}
        className="rounded border-slate-300" />
    ), render: (r) => (
      <input type="checkbox" checked={selected.has(r.id)}
        onChange={() => toggleSel(r.id)}
        data-testid={`contact-sel-${r.id}`}
        onClick={(e) => e.stopPropagation()}
        className="rounded border-slate-300" />
    ) },
    { key: "nome", label: "Nome", render: (r) => (
      <div className="flex items-center gap-1.5">
        <button onClick={() => setDrawer(r)} data-testid={`contact-drawer-open-${r.id}`}
          className="font-semibold text-navy hover:text-blue hover:underline text-left"><HighlightText text={r.nome} query={q} /></button>
        {r.principal && <span title="Contacto principal" className="text-emerald-600 text-xs">*</span>}
      </div>
    ) },
    { key: "cargo", label: "Cargo", render: (r) => r.cargo || r.departamento || "-" },
    { key: "emails", label: "Email", render: (r) => (r.emails || []).length > 0
      ? (r.emails || []).map((e, i) => <span key={i}>{i > 0 && ", "}<HighlightText text={e} query={q} /></span>)
      : "-" },
    { key: "telefones", label: "Telefone", render: (r) => {
      const nums = [...(r.telefones || []), r.telefone, r.telemovel].filter(Boolean);
      const uniq = [...new Set(nums)];
      return uniq.length > 0 ? uniq.join(", ") : "-";
    } },
    { key: "ciclo_vida", label: "Ciclo", render: (r) => <Badge value={r.ciclo_vida} /> },
    { key: "marketing_optin", label: "Marketing", render: (r) => r.marketing_optin ? <Badge value="OPTIN" tone="green" /> : <span className="text-[10px] text-muted2">-</span> },
    { key: "account_id", label: "Conta CRM", render: (r) => (
      r.account_id ? (
        <button onClick={() => nav(`/app/crm/contas/${r.account_id}`)}
          data-testid={`contact-account-link-${r.id}`} title={r.account_nome}
          className="inline-flex items-center gap-1 text-blue hover:underline text-sm">
          <Building2 size={11} /> {r.account_nome || "Ver conta"}
          {r.account_tipo && <span className="text-[9px] uppercase text-slate-400 ml-1">{r.account_tipo}</span>}
        </button>
      ) : <span className="text-slate-400 text-xs italic">sem conta</span>
    ) },
    { key: "criado_em", label: "Criado", render: (r) => dt(r.criado_em) },
    { key: "acoes", label: "", render: (r) => (
      <div className="flex items-center gap-1">
        <button onClick={() => setDrawer(r)} data-testid={`contact-drawer-btn-${r.id}`}
          title="Ver detalhes" className="text-slate-700 hover:bg-slate-100 rounded p-1"><Eye size={15} /></button>
        {tab === "ativos" && (
          <button onClick={() => openEdit(r)} data-testid={`contact-edit-${r.id}`}
            title="Editar" className="text-navy hover:bg-tint rounded p-1"><Pencil size={13} /></button>
        )}
        <button onClick={() => openHistory(r)} data-testid={`contact-history-${r.id}`}
          title="Historial de alteracoes" className="text-slate-600 hover:bg-slate-100 rounded p-1"><History size={13} /></button>
        {isSuper && tab === "ativos" && (
          <button onClick={() => setToDelete(r)} data-testid={`contact-delete-${r.id}`}
            title="Eliminar / inativar" className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={13} /></button>
        )}
        {isSuper && tab === "inativos" && (
          <>
            <button onClick={() => reactivate(r)} data-testid={`contact-reactivate-${r.id}`}
              title="Reativar contacto" className="text-emerald-600 hover:bg-emerald-50 rounded p-1"><RotateCcw size={13} /></button>
            <button onClick={() => setToDelete(r)} data-testid={`contact-delete-${r.id}`}
              title="Eliminar definitivamente" className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={13} /></button>
          </>
        )}
      </div>
    ) },
  ];

  return (
    <>
      <PageHeader overline="CRM" title="Contactos"
        action={selected.size > 0 && (
          <div className="flex items-center gap-2">
            <Btn onClick={() => setBulkOpen(true)} testid="contacts-bulk-btn">
              <CheckSquare size={13} /> Actualizar {selected.size} contacto{selected.size > 1 ? "s" : ""}
            </Btn>
            {isSuper && tab === "ativos" && (
              <Btn variant="ghost" onClick={() => setBulkDeactivateOpen(true)} testid="contacts-bulk-deactivate-btn" className="!text-red-600 !border-red-300 hover:!bg-red-50">
                <UserX size={13} /> Inativar {selected.size} contacto{selected.size > 1 ? "s" : ""}
              </Btn>
            )}
          </div>
        )}
      />
      <div className="flex items-center gap-1 mb-4" data-testid="contacts-tabs">
        <button onClick={() => setTab("ativos")} data-testid="contacts-tab-ativos"
          className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors ${tab === "ativos" ? "bg-blue text-white" : "bg-tint text-navy hover:bg-slate-200"}`}>
          Ativos
        </button>
        <button onClick={() => setTab("inativos")} data-testid="contacts-tab-inativos"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors ${tab === "inativos" ? "bg-blue text-white" : "bg-tint text-navy hover:bg-slate-200"}`}>
          <UserX size={13} /> Inativos
        </button>
      </div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Pesquisar por nome ou email" data-testid="contact-search"
            className="w-full pl-9 pr-3 py-2 border border-tint rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue" />
        </div>
        <Btn variant="ghost" onClick={load} testid="contact-search-btn">Pesquisar</Btn>
        <select value={sortMode} onChange={(e) => setSortMode(e.target.value)} data-testid="contacts-sort-mode"
          className="px-3 py-2 border border-tint rounded-lg bg-white text-sm">
          <option value="alpha">Ordenar: Alfabetica (A-Z)</option>
          <option value="recent">Ordenar: Mais recentes</option>
        </select>
        <select value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)}
          data-testid="contacts-account-filter"
          className="px-3 py-2 border border-tint rounded-lg bg-white text-sm min-w-[220px]">
          <option value="">Todas as contas ({rows.length})</option>
          <option value="__none__">-- Sem conta CRM --</option>
          {accountsOptions.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
        </select>
        {accountFilter && (
          <button onClick={() => setAccountFilter("")} data-testid="contacts-clear-filter"
            className="text-[11px] text-blue hover:underline">Limpar filtro</button>
        )}
      </div>
      <Table testid="contacts-table" columns={columns} rows={filteredRows} />
      {drawer && (
        <ContactDrawer contact={drawer} open={!!drawer} onClose={() => setDrawer(null)} />
      )}

      <Modal open={bulkOpen} onClose={() => setBulkOpen(false)} title={`Actualizar ${selected.size} contactos`} testid="contacts-bulk-modal">
        <p className="text-[12px] text-muted2 mb-3">Apenas os campos preenchidos serao aplicados. Cada alteracao fica registada no historial de cada contacto.</p>
        <div>
          <label className="block text-xs uppercase text-muted2 mb-1">Departamento</label>
          <select value={bulk.departamento} onChange={(e) => setBulk({ ...bulk, departamento: e.target.value })}
            data-testid="bulk-departamento" className="w-full px-3 py-2 border border-tint rounded-lg bg-white text-sm mb-3">
            <option value="">- Nao alterar -</option>
            {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase text-muted2 mb-1">Marketing opt-in</label>
          <select value={bulk.marketing_optin === null ? "" : String(bulk.marketing_optin)}
            onChange={(e) => setBulk({ ...bulk, marketing_optin: e.target.value === "" ? null : e.target.value })}
            data-testid="bulk-marketing" className="w-full px-3 py-2 border border-tint rounded-lg bg-white text-sm mb-3">
            <option value="">- Nao alterar -</option>
            <option value="true">Aceita marketing</option>
            <option value="false">Nao aceita marketing</option>
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Btn variant="ghost" onClick={() => setBulkOpen(false)}>Cancelar</Btn>
          <Btn onClick={applyBulk} testid="bulk-apply">Aplicar</Btn>
        </div>
      </Modal>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={`Editar ${edit?.nome || "contacto"}`} testid="contact-edit-modal" size="lg">
        {edit && (
          <>
            {edit.account_nome && (
              <div className="mb-3 flex items-center gap-2 bg-blue/5 border border-blue/20 rounded-md px-3 py-2 text-sm">
                <Building2 size={13} className="text-blue" />
                <span className="text-navy font-semibold flex-1">{edit.account_nome}</span>
                <span className="text-[10px] uppercase text-slate-500">{edit.account_tipo}</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome" value={edit.nome || ""} onChange={(v) => setEdit({ ...edit, nome: v })} testid="edit-contact-nome" />
              <div>
                <label className="block text-xs uppercase text-muted2 mb-1">Departamento</label>
                <select value={edit.departamento || ""}
                  onChange={(e) => setEdit({ ...edit, departamento: e.target.value })}
                  data-testid="edit-contact-departamento"
                  className="w-full px-3 py-2 border border-tint rounded-lg bg-white text-sm">
                  <option value="">- Sem departamento -</option>
                  {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <Field label="Cargo" value={edit.cargo || ""} onChange={(v) => setEdit({ ...edit, cargo: v })} testid="edit-contact-cargo" />
              <Field label="Email" value={edit.email || ""} onChange={(v) => setEdit({ ...edit, email: v })} testid="edit-contact-email" />
              <Field label="Telefone" value={edit.telefone || ""} onChange={(v) => setEdit({ ...edit, telefone: v })} testid="edit-contact-telefone" />
              <Field label="Telemovel" value={edit.telemovel || ""} onChange={(v) => setEdit({ ...edit, telemovel: v })} testid="edit-contact-telemovel" />
            </div>
            <label className="flex items-center gap-2 text-sm text-navy cursor-pointer mt-2">
              <input type="checkbox" checked={!!edit.principal}
                onChange={(e) => setEdit({ ...edit, principal: e.target.checked })}
                data-testid="edit-contact-principal" className="rounded border-slate-300" />
              Contacto principal
            </label>
            <label className="flex items-center gap-2 text-sm text-navy cursor-pointer mt-2">
              <input type="checkbox" checked={!!edit.marketing_optin}
                onChange={(e) => setEdit({ ...edit, marketing_optin: e.target.checked })}
                data-testid="edit-contact-optin" className="rounded border-slate-300 accent-emerald-600" />
              Marketing opt-in
            </label>
            <TextArea label="Notas" value={edit.notas || ""} onChange={(v) => setEdit({ ...edit, notas: v })} testid="edit-contact-notas" />
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setEdit(null)}>Cancelar</Btn>
              <Btn onClick={saveEdit} testid="edit-contact-save">Guardar</Btn>
            </div>
          </>
        )}
      </Modal>

      <Modal open={!!historyFor} onClose={() => setHistoryFor(null)} title={`Historial de ${historyFor?.nome || ""}`} testid="contact-history-modal" size="lg">
        {historyFor && historyFor.events === null && <p className="text-sm text-muted2">A carregar...</p>}
        {historyFor && Array.isArray(historyFor.events) && historyFor.events.length === 0 && (
          <p className="text-sm text-muted2 italic">Sem eventos registados.</p>
        )}
        {historyFor && Array.isArray(historyFor.events) && historyFor.events.length > 0 && (
          <ul className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="contact-history-list">
            {historyFor.events.map((e, i) => (
              <li key={i} className="border border-tint rounded-md px-3 py-2 text-[13px]">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-semibold text-navy">{e.action === "bulk_update" ? "Actualizacao em lote" : "Actualizacao"}</span>
                  <span className="text-[11px] text-slate-500">{new Date(e.at).toLocaleString("pt-PT")}</span>
                </div>
                <div className="text-[11px] text-slate-500 mb-1">por <b>{e.actor_email || "?"}</b></div>
                {(e.diffs || []).map((d, j) => (
                  <div key={j} className="text-[12px] font-mono bg-slate-50 px-2 py-1 rounded mt-1 border border-tint">
                    <span className="text-slate-500">{d.campo}:</span>{" "}
                    {"antes" in d && <span className="text-red-700 line-through">{JSON.stringify(d.antes)}</span>}
                    {"antes" in d && <span> -&gt; </span>}
                    <span className="text-emerald-700">{JSON.stringify(d.depois)}</span>
                  </div>
                ))}
              </li>
            ))}
          </ul>
        )}
      </Modal>

      <Modal open={!!toDelete} onClose={() => setToDelete(null)} title="Eliminar contacto" testid="contact-delete-modal">
        {toDelete && (
          <>
            <p className="text-sm text-navy">
              {tab === "inativos"
                ? <>Tem a certeza que quer eliminar <b>{toDelete.nome}</b> definitivamente? Esta acao nao pode ser revertida.</>
                : <>Se <b>{toDelete.nome}</b> nao tiver nenhuma atividade registada, sera eliminado definitivamente. Caso contrario, sera apenas marcado como inativo e podera ser eliminado mais tarde.</>}
            </p>
            <div className="flex justify-end gap-2 pt-4">
              <Btn variant="ghost" onClick={() => setToDelete(null)} testid="contact-delete-cancel">Cancelar</Btn>
              <Btn onClick={confirmDelete} testid="contact-delete-confirm" variant="danger">Eliminar</Btn>
            </div>
          </>
        )}
      </Modal>

      <Modal open={bulkDeactivateOpen} onClose={() => setBulkDeactivateOpen(false)} title="Inativar contactos em lote" testid="contacts-bulk-deactivate-modal">
        <p className="text-sm text-navy">
          Tem a certeza que quer marcar <b>{selected.size}</b> contacto{selected.size > 1 ? "s" : ""} como inativo{selected.size > 1 ? "s" : ""}?
          Deixam de aparecer na lista de Ativos e passam para a tab Inativos, onde podem ser reativados mais tarde.
        </p>
        <div className="flex justify-end gap-2 pt-4">
          <Btn variant="ghost" onClick={() => setBulkDeactivateOpen(false)} testid="contacts-bulk-deactivate-cancel">Cancelar</Btn>
          <Btn onClick={confirmBulkDeactivate} testid="contacts-bulk-deactivate-confirm" variant="danger">Inativar</Btn>
        </div>
      </Modal>
    </>
  );
}
